import { useSyncExternalStore } from 'react';

export enum ThemeMode {
  Light = 'light',
  Dark = 'dark',
  HighContrast = 'highContrast',
}

interface UseProviderTheme {
  mode: ThemeMode;
}

const getThemeMode = (): ThemeMode => {
  if (typeof document === 'undefined') return ThemeMode.Light;
  const body = document.querySelector('body');
  if (!body) return ThemeMode.Light;

  if (body.classList.contains('darkTheme')) return ThemeMode.Dark;
  if (body.classList.contains('highContrastTheme')) return ThemeMode.HighContrast;
  return ThemeMode.Light;
};

let currentMode = getThemeMode();
const listeners = new Set<() => void>();
let observer: MutationObserver | null = null;

const subscribe = (callback: () => void): (() => void) => {
  if (typeof document === 'undefined') return () => {};

  // Lazily create a single shared MutationObserver. With per-subscriber
  // observers, only the first one to fire would see currentMode !==
  // newMode and notify React — the rest would skip the callback and
  // their components would render with stale theme state.
  if (observer === null) {
    // The body class may have changed before any subscriber existed.
    // Refresh from the DOM so the very first getSnapshot is fresh.
    currentMode = getThemeMode();

    observer = new MutationObserver(() => {
      const newMode = getThemeMode();
      if (newMode === currentMode) return;
      currentMode = newMode;
      listeners.forEach(listener => listener());
    });

    const body = document.querySelector('body');
    if (body) {
      observer.observe(body, { attributes: true, attributeFilter: ['class'] });
    }
  }

  listeners.add(callback);

  return () => {
    listeners.delete(callback);
    if (listeners.size === 0 && observer) {
      observer.disconnect();
      observer = null;
    }
  };
};

const getSnapshot = (): ThemeMode => currentMode;
const getServerSnapshot = (): ThemeMode => ThemeMode.Light;

/**
 * Returns the current theme mode (light, dark, or high contrast)
 * by observing the body element's CSS classes.
 *
 * @return {*}  {UseProviderTheme}
 */
export const useProviderTheme = (): UseProviderTheme => {
  const mode = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return { mode };
};
